import { FC, KeyboardEvent, useRef } from 'react'
import styled from 'styled-components'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'

import { AuthenticatedHeader } from 'app/features/common/headers/authenticatedHeader'
import { RouteUrl, IRouteDefinition } from 'app/routes'
import { ComponentHeights, Text } from '@tuhi/components'
import { media } from '@tuhi/components/src/theme/breakpoints'

import { activeTabIndex, nextTabIndex } from './adminShellView'

/**
 * Admin console shell (ACA-140 Slice A / ACA-145).
 *
 * A single tabbed layout that replaces the previous section-card landing
 * (`AdminLanding`): Users / Templates / Forms are rendered as tabs, and the
 * active screen mounts under the tab via the routed `<Outlet />`. The whole
 * subtree is still gated by `AdminRoute` (in `router.tsx`), so this component
 * only ever renders for an admin.
 *
 * Navigation is route-driven, so the active tab is derived from the URL and
 * deep-linking to a tab's route (or refreshing on it) selects that tab. The
 * tablist implements the WAI-ARIA tabs pattern with roving tabindex and
 * Left/Right/Home/End keyboard navigation (automatic activation — moving focus
 * navigates to that tab's route).
 *
 * Each screen (`UserManagement`, `TemplateManagement`, `FormManagement`) renders
 * only its own content and reserves space for this header + tab bar via its
 * `Scroll` offsets (`AuthenticatedHeader` + `AdminTabs`).
 */

interface AdminTab {
    route: IRouteDefinition
    label: string
}

const TABS: AdminTab[] = [
    { route: RouteUrl.AdminUsers, label: 'Users' },
    { route: RouteUrl.AdminTemplates, label: 'Templates' },
    { route: RouteUrl.AdminForms, label: 'Forms' },
    { route: RouteUrl.AdminLogs, label: 'Logs' },
]

const Container = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    min-height: 0;
`

const TabPanel = styled.div`
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
`

const TabList = styled.div`
    display: flex;
    align-items: stretch;
    gap: 8px;
    height: ${ComponentHeights.AdminTabs}px;
    box-sizing: border-box;
    padding: 0 16px;
    box-shadow: inset 0 -1px 0 ${(p) => p.theme.colors.tertiary.tertiary100};
    overflow-x: auto;

    ${media.mobile} {
        gap: 0;
    }
`

const Tab = styled.button<{ $active: boolean }>`
    position: relative;
    border: 0;
    background: none;
    cursor: pointer;
    padding: 0 16px;
    display: flex;
    align-items: center;
    white-space: nowrap;
    box-shadow: ${(p) =>
        p.$active
            ? `inset 0 -2px 0 ${p.theme.colors.primary.default}`
            : 'none'};

    &:focus-visible {
        outline: 2px solid ${(p) => p.theme.colors.primary.default};
        outline-offset: -2px;
        border-radius: 4px;
    }
`

export const AdminShell: FC = () => {
    const location = useLocation()
    const navigate = useNavigate()
    const tabRefs = useRef<(HTMLButtonElement | null)[]>([])

    const activeIndex = activeTabIndex(
        location.pathname,
        TABS.map((t) => t.route.path)
    )

    const activateTab = (index: number): void => {
        const tab = TABS[index]
        if (!tab) return
        tabRefs.current[index]?.focus()
        navigate(tab.route.path)
    }

    const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>): void => {
        const next = nextTabIndex(event.key, activeIndex, TABS.length)
        if (next === null) return
        event.preventDefault()
        activateTab(next)
    }

    return (
        <Container>
            <AuthenticatedHeader title="Admin" />

            <TabList
                role="tablist"
                aria-label="Admin sections"
                data-testid="admin-tabs"
            >
                {TABS.map((tab, index) => {
                    const active = index === activeIndex
                    return (
                        <Tab
                            key={tab.route.path}
                            ref={(el) => (tabRefs.current[index] = el)}
                            role="tab"
                            id={`admin-tab-${tab.route.segment}`}
                            aria-selected={active}
                            aria-controls="admin-tabpanel"
                            tabIndex={active ? 0 : -1}
                            $active={active}
                            data-testid={`admin-tab-${tab.route.segment}`}
                            onClick={() => activateTab(index)}
                            onKeyDown={onKeyDown}
                        >
                            <Text
                                size="medium"
                                $weight={active ? 'semiBold' : 'regular'}
                                color={active ? 'primary' : 'secondary'}
                            >
                                {tab.label}
                            </Text>
                        </Tab>
                    )
                })}
            </TabList>

            <TabPanel
                role="tabpanel"
                id="admin-tabpanel"
                aria-labelledby={`admin-tab-${TABS[activeIndex].route.segment}`}
            >
                <Outlet />
            </TabPanel>
        </Container>
    )
}
